/**
 * MarketplacePage — /v3/crm/marketplace
 *
 * V3-native Lead Marketplace. Thin premium presentation over the existing
 * /api/marketplace/* router. Three sections:
 *   1. Balance + top-of-funnel copy
 *   2. Credit Packs — click any pack → Stripe Checkout → land back with credits
 *   3. Redeem — pick a market + filter preset + quantity → leads land in CRM
 *   4. History — recent purchases + redemptions
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ShoppingCart, Coins, Sparkles, Loader2, CheckCircle2, ArrowUpRight,
  MapPin, Filter, Send, History, Zap, TrendingUp, Info, Star, X,
  Save, Play, Trash2, Crown, ShieldCheck, MessageSquare, Phone,
} from "lucide-react";
import DailyBusinessScore from "@/pages/v3/DailyBusinessScore";

// ─────────────────────────────────────────── helpers ────────────────────

function timeAgo(iso) {
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return ""; }
}

// ─────────────────────────────────────────── page ──────────────────────

export default function MarketplacePage() {
  const nav = useNavigate();
  const location = useLocation();

  const [packs, setPacks] = useState([]);
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [savedFilters, setSavedFilters] = useState([]);
  const [filters, setFilters] = useState([]);
  const [balance, setBalance] = useState(null);
  const [history, setHistory] = useState({ topups: [], redemptions: [] });
  const [loading, setLoading] = useState(true);
  const [checkoutBusy, setCheckoutBusy] = useState(null);
  const [subBusy, setSubBusy] = useState(null);
  const [redeemBusy, setRedeemBusy] = useState(false);
  const [savedBusy, setSavedBusy] = useState(false);
  const [verifyBusy, setVerifyBusy] = useState(null);
  const [smsRedemption, setSmsRedemption] = useState(null); // { id, leads: [] }
  const [smsLoading, setSmsLoading] = useState(false);

  const [market, setMarket] = useState("");
  const [filterKey, setFilterKey] = useState("");
  const [quantity, setQuantity] = useState(10);
  const [saveLabel, setSaveLabel] = useState("");

  // Bootstrap everything in parallel.
  const loadAll = useCallback(async () => {
    try {
      const [p, pl, s, sf, f, b, h] = await Promise.all([
        api.get("/marketplace/packs").then((r) => r.data),
        api.get("/marketplace/plans").then((r) => r.data),
        api.get("/marketplace/subscription").then((r) => r.data).catch(() => ({ subscription: null })),
        api.get("/marketplace/saved-filters").then((r) => r.data).catch(() => ({ items: [] })),
        api.get("/marketplace/filters").then((r) => r.data),
        api.get("/marketplace/balance").then((r) => r.data),
        api.get("/marketplace/history").then((r) => r.data),
      ]);
      setPacks(p?.packs || []);
      setPlans(pl?.plans || []);
      setSubscription(s?.subscription || null);
      setSavedFilters(sf?.items || []);
      setFilters(f?.filters || []);
      setBalance(b || null);
      setHistory(h || { topups: [], redemptions: [] });
      if (f?.filters?.length && !filterKey) setFilterKey(f.filters[0].key);
    } catch {
      toast.error("Couldn't load the marketplace");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  // Handle Stripe redirect (?checkout=success|cancel).
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const status = p.get("checkout");
    if (status === "success") {
      toast.success("Credits added! Refreshing balance…");
      // Small delay so the webhook has time to process before refetching.
      const t = setTimeout(loadAll, 1500);
      // Clear the query so we don't fire again on re-mount.
      nav("/v3/crm/marketplace", { replace: true });
      return () => clearTimeout(t);
    }
    if (status === "cancel") {
      toast("Checkout cancelled — no charge", { icon: "🛒" });
      nav("/v3/crm/marketplace", { replace: true });
    }
    return undefined;
  }, [location.search]);

  const buyPack = async (pack) => {
    if (checkoutBusy) return;
    setCheckoutBusy(pack.key);
    try {
      const { data } = await api.post("/marketplace/checkout", { pack: pack.key });
      if (!data?.checkout_url) throw new Error("No checkout URL");
      window.location.href = data.checkout_url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't start checkout");
      setCheckoutBusy(null);
    }
  };

  const redeem = async () => {
    if (!market.trim()) { toast.error("Enter a market (city, state or ZIP)."); return; }
    if (!filterKey) { toast.error("Pick a filter preset."); return; }
    setRedeemBusy(true);
    try {
      const { data } = await api.post("/marketplace/redeem", {
        market: market.trim(),
        filter_preset: filterKey,
        quantity: Number(quantity) || 1,
      });
      toast.success(`${data.leads_delivered} lead${data.leads_delivered === 1 ? "" : "s"} dropped into your CRM`);
      setBalance((b) => (b ? { ...b, balance: data.balance, used_total: b.used_total + data.leads_delivered } : b));
      window.dispatchEvent(new Event("credits-changed"));
      // Refresh history so the redemption shows up.
      const h = await api.get("/marketplace/history").then((r) => r.data);
      setHistory(h || history);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Redeem failed");
    } finally {
      setRedeemBusy(false);
    }
  };

  const subscribe = async (planKey) => {
    if (subBusy) return;
    setSubBusy(planKey);
    try {
      const { data } = await api.post("/marketplace/subscribe", { plan: planKey });
      setSubscription(data.subscription);
      // Grant is applied immediately — refresh balance + history
      const [b, h] = await Promise.all([
        api.get("/marketplace/balance").then((r) => r.data),
        api.get("/marketplace/history").then((r) => r.data),
      ]);
      setBalance(b);
      setHistory(h);
      toast.success(`Subscribed to ${data.subscription.plan.toUpperCase()} — credits added!`);
      window.dispatchEvent(new Event("credits-changed"));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Subscribe failed");
    } finally { setSubBusy(null); }
  };

  const cancelPlan = async () => {
    if (!window.confirm("Cancel your active plan? Credits already granted will remain.")) return;
    try {
      await api.post("/marketplace/cancel-plan");
      setSubscription(null);
      toast.success("Plan cancelled");
    } catch { toast.error("Couldn't cancel"); }
  };

  const saveCurrentFilter = async () => {
    if (!market.trim() || !filterKey || !saveLabel.trim()) {
      toast.error("Give the filter a name, market, and preset first.");
      return;
    }
    setSavedBusy(true);
    try {
      const { data } = await api.post("/marketplace/saved-filters", {
        label: saveLabel.trim(),
        market: market.trim(),
        filter_preset: filterKey,
        quantity: Number(quantity),
        auto_deliver: !!subscription,
      });
      setSavedFilters((sf) => [data, ...sf]);
      setSaveLabel("");
      toast.success("Filter saved");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally { setSavedBusy(false); }
  };

  const deleteSavedFilter = async (id) => {
    try {
      await api.delete(`/marketplace/saved-filters/${id}`);
      setSavedFilters((sf) => sf.filter((f) => f.id !== id));
      toast.success("Filter removed");
    } catch { toast.error("Couldn't delete"); }
  };

  const runSavedFilter = async (id) => {
    try {
      const { data } = await api.post(`/marketplace/saved-filters/${id}/run-now`);
      toast.success(`${data.leads_delivered} leads pulled → CRM`);
      const [b, h, sf] = await Promise.all([
        api.get("/marketplace/balance").then((r) => r.data),
        api.get("/marketplace/history").then((r) => r.data),
        api.get("/marketplace/saved-filters").then((r) => r.data),
      ]);
      setBalance(b); setHistory(h); setSavedFilters(sf.items || []);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Run failed");
    }
  };

  const verifyRedemption = async (redemptionId) => {
    if (verifyBusy) return;
    setVerifyBusy(redemptionId);
    try {
      const { data } = await api.post(`/marketplace/redemption/${redemptionId}/verify`);
      toast.success(`${data.verified} lead${data.verified === 1 ? "" : "s"} AI-verified · quota ${data.quota}`);
      const h = await api.get("/marketplace/history").then((r) => r.data);
      setHistory(h);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Verify failed");
    } finally { setVerifyBusy(null); }
  };

  const openSmsModal = async (redemptionId) => {
    setSmsLoading(true);
    setSmsRedemption({ id: redemptionId, leads: [] });
    try {
      const { data } = await api.get(`/marketplace/redemption/${redemptionId}/leads`);
      setSmsRedemption({ id: redemptionId, leads: data.leads || [] });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't load verified leads");
      setSmsRedemption(null);
    } finally { setSmsLoading(false); }
  };

  const activeFilter = useMemo(
    () => filters.find((f) => f.key === filterKey) || null,
    [filters, filterKey],
  );
  const bal = balance?.balance ?? 0;

  return (
    <div className="space-y-6" data-testid="marketplace-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-amber-600">
            CRM · Sourcing
          </div>
          <h1 className="mt-1 text-[32px] font-bold text-slate-900 tracking-tight leading-tight">
            Lead Marketplace
          </h1>
          <p className="mt-2 text-slate-600 text-[14px] max-w-2xl">
            Buy credit packs, redeem them for motivated-seller leads pulled straight
            into your Leads CRM. No subscription required — credits never expire.
          </p>
        </div>
        {/* Balance chip */}
        <div
          data-testid="marketplace-balance-chip"
          className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white px-4 py-2 shadow-lg shadow-amber-500/10 min-w-[220px] border border-amber-500/30"
        >
          <Coins className="w-4 h-4 text-amber-400" />
          <div className="flex-1">
            <div className="text-[9px] uppercase tracking-widest font-bold text-amber-400/80 leading-none">
              Credit balance
            </div>
            <div className="text-[18px] font-black tabular-nums leading-tight" data-testid="marketplace-balance-value">
              {loading ? "—" : bal.toLocaleString()}
            </div>
          </div>
          <div className="text-[10px] text-slate-400 tabular-nums text-right leading-tight">
            <div>{balance?.purchased_total || 0} bought</div>
            <div>{balance?.used_total || 0} used</div>
          </div>
        </div>
      </div>

      {/* Packs */}
      <section
        data-testid="marketplace-packs"
        className="rounded-2xl border border-slate-200/70 bg-white shadow-sm overflow-hidden"
      >
        <div className="h-1 w-full" style={{ backgroundColor: "#F59E0B" }} />
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg grid place-items-center text-white shadow-md shrink-0"
               style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}>
            <ShoppingCart className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-600 leading-none">
              Buy credits
            </div>
            <div className="text-[15px] font-bold text-slate-900 leading-tight">
              Credit Packs
            </div>
          </div>
        </div>
        {loading ? (
          <div className="p-10 grid place-items-center text-slate-500 text-sm">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 p-5">
            {packs.map((pack) => (
              <PackCard key={pack.key}
                        pack={pack}
                        busy={checkoutBusy === pack.key}
                        onBuy={() => buyPack(pack)} />
            ))}
          </div>
        )}
      </section>

      {/* Subscription Plans (P2 Path 2) */}
      <section
        data-testid="marketplace-plans"
        className="rounded-2xl border border-slate-200/70 bg-white shadow-sm overflow-hidden"
      >
        <div className="h-1 w-full" style={{ backgroundImage: "linear-gradient(90deg, #8B5CF6, #F59E0B)" }} />
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2 flex-wrap">
          <div className="h-8 w-8 rounded-lg grid place-items-center text-white shadow-md shrink-0"
               style={{ backgroundImage: "linear-gradient(135deg, #8B5CF6, #6D28D9)" }}>
            <Crown className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest font-bold text-purple-600 leading-none">
              Monthly plans · Auto-delivery
            </div>
            <div className="text-[15px] font-bold text-slate-900 leading-tight">
              Subscribe for recurring credits + AI-verified leads
            </div>
          </div>
          {subscription && (
            <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-[11px] font-bold text-emerald-700"
                 data-testid="marketplace-active-plan-chip">
              <ShieldCheck className="w-3.5 h-3.5" />
              {subscription.plan.toUpperCase()} · active
              <button type="button" onClick={cancelPlan}
                      data-testid="marketplace-cancel-plan-btn"
                      className="ml-1 text-rose-600 hover:text-rose-700 text-[10px] uppercase tracking-wider">
                Cancel
              </button>
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-5">
          {plans.map((plan) => (
            <PlanCard key={plan.key}
                      plan={plan}
                      isActive={subscription?.plan === plan.key}
                      busy={subBusy === plan.key}
                      onSubscribe={() => subscribe(plan.key)} />
          ))}
        </div>
      </section>

      {/* Saved Filters (P2 Path 2) */}
      {(subscription || savedFilters.length > 0) && (
        <section
          data-testid="marketplace-saved-filters"
          className="rounded-2xl border border-slate-200/70 bg-white shadow-sm overflow-hidden"
        >
          <div className="h-1 w-full" style={{ backgroundColor: "#8B5CF6" }} />
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2 flex-wrap">
            <div className="h-8 w-8 rounded-lg grid place-items-center text-white shadow-md shrink-0"
                 style={{ backgroundImage: "linear-gradient(135deg, #8B5CF6, #6D28D9)" }}>
              <Save className="w-4 h-4" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest font-bold text-purple-600 leading-none">
                Reusable searches
              </div>
              <div className="text-[15px] font-bold text-slate-900 leading-tight">
                Saved Filters ({savedFilters.length})
              </div>
            </div>
          </div>
          <div className="p-5 space-y-3">
            {/* Save-as row (uses the redeem-panel state) */}
            <div className="flex items-center gap-2 flex-wrap rounded-lg bg-slate-50/60 border border-slate-100 p-3">
              <input type="text" value={saveLabel}
                     onChange={(e) => setSaveLabel(e.target.value)}
                     placeholder="Name this search (e.g. ATX Absentee)"
                     data-testid="marketplace-save-filter-label"
                     className="flex-1 min-w-[200px] rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
              <div className="text-[11px] text-slate-500">
                Saves: <b>{market || "—"}</b> · <b>{activeFilter?.label || "—"}</b> · <b>{quantity}</b>
              </div>
              <button type="button" onClick={saveCurrentFilter}
                      disabled={savedBusy}
                      data-testid="marketplace-save-filter-btn"
                      className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[12.5px] font-semibold px-3 py-1.5 disabled:opacity-60">
                {savedBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save filter
              </button>
            </div>
            {/* List */}
            {savedFilters.length === 0 ? (
              <div className="text-[12px] text-slate-500 text-center py-6">
                No saved filters yet — build a redeem query above and click Save.
              </div>
            ) : (
              <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                {savedFilters.map((f) => (
                  <div key={f.id}
                       data-testid={`marketplace-saved-${f.id}`}
                       className="p-3 flex items-center gap-2 hover:bg-purple-50/40 transition-colors">
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-bold text-slate-900 truncate flex items-center gap-1.5">
                        {f.label}
                        {f.auto_deliver && (
                          <span className="text-[9px] uppercase tracking-widest font-black px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700"
                                data-testid={`marketplace-saved-${f.id}-auto`}>
                            Auto
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-500 truncate">
                        {f.market} · {f.filter_preset} · {f.quantity} leads
                        {f.last_delivered_at ? ` · last ran ${timeAgo(f.last_delivered_at)}` : ""}
                      </div>
                    </div>
                    <button type="button" onClick={() => runSavedFilter(f.id)}
                            data-testid={`marketplace-saved-${f.id}-run`}
                            title="Run this filter now"
                            className="inline-flex items-center gap-1 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white text-[11px] font-semibold px-2 py-1">
                      <Play className="w-3 h-3" /> Run now
                    </button>
                    <button type="button" onClick={() => deleteSavedFilter(f.id)}
                            data-testid={`marketplace-saved-${f.id}-delete`}
                            className="p-1 rounded text-slate-400 hover:text-rose-600">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Redeem — DUPLICATE BELOW (need to keep single instance) */}
      <section
        data-testid="marketplace-redeem"
        className="rounded-2xl border border-slate-200/70 bg-white shadow-sm overflow-hidden"
      >
        <div className="h-1 w-full" style={{ backgroundColor: "#10B981" }} />
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg grid place-items-center text-white shadow-md shrink-0"
               style={{ backgroundImage: "linear-gradient(135deg, #10B981, #059669)" }}>
            <Send className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-600 leading-none">
              Spend credits
            </div>
            <div className="text-[15px] font-bold text-slate-900 leading-tight">
              Pull leads into your CRM
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-4 p-5">
          {/* Form */}
          <div className="space-y-3">
            <FormGroup label="Market · City, State or ZIP">
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input type="text" value={market}
                       onChange={(e) => setMarket(e.target.value)}
                       placeholder="Austin, TX  or  78701"
                       data-testid="marketplace-market-input"
                       className="w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400" />
              </div>
            </FormGroup>
            <FormGroup label="Filter preset">
              <div className="grid grid-cols-1 gap-1.5">
                {filters.map((f) => (
                  <label key={f.key}
                         data-testid={`marketplace-filter-${f.key}`}
                         className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${
                           filterKey === f.key
                             ? "border-amber-400 bg-amber-50/50"
                             : "border-slate-200 hover:bg-slate-50"
                         }`}>
                    <input type="radio" name="filter" value={f.key}
                           checked={filterKey === f.key}
                           onChange={() => setFilterKey(f.key)}
                           className="w-4 h-4 text-amber-600 focus:ring-amber-400" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] font-bold text-slate-900 truncate">{f.label}</div>
                      <div className="text-[10.5px] text-slate-500 truncate">{f.blurb}</div>
                    </div>
                  </label>
                ))}
              </div>
            </FormGroup>
            <FormGroup label={`Quantity · ${quantity} credit${quantity === 1 ? "" : "s"}`}>
              <input type="range" min="1" max={Math.min(100, Math.max(1, bal || 100))}
                     value={quantity}
                     onChange={(e) => setQuantity(Number(e.target.value))}
                     data-testid="marketplace-quantity-slider"
                     className="w-full accent-amber-500" />
              <div className="flex justify-between text-[10px] text-slate-500 tabular-nums mt-0.5">
                <span>1</span>
                <span>{Math.min(100, Math.max(1, bal || 100))}</span>
              </div>
            </FormGroup>
            <button type="button"
                    onClick={redeem}
                    disabled={redeemBusy || bal < quantity}
                    data-testid="marketplace-redeem-btn"
                    title={bal < quantity ? "Not enough credits — buy a pack" : ""}
                    className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg text-white text-[13px] font-semibold px-4 py-2.5 shadow-md disabled:opacity-60"
                    style={{ backgroundImage: "linear-gradient(135deg, #10B981, #059669)" }}>
              {redeemBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              Spend {quantity} credit{quantity === 1 ? "" : "s"} · Pull leads
            </button>
            {bal < quantity && (
              <div className="text-[11px] text-rose-600 flex items-start gap-1.5">
                <Info className="w-3 h-3 mt-0.5 shrink-0" />
                You have <b>{bal}</b> credit{bal === 1 ? "" : "s"}. Buy a pack above.
              </div>
            )}
          </div>

          {/* Preview */}
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-3">
              <Sparkles className="w-3 h-3" /> What you get
            </div>
            {activeFilter ? (
              <div className="space-y-3">
                <div>
                  <div className="text-[15px] font-bold text-slate-900">
                    {activeFilter.label}
                  </div>
                  <div className="text-[12px] text-slate-600 leading-relaxed mt-1">
                    {activeFilter.blurb}
                  </div>
                </div>
                <div className="border-t border-slate-200 pt-3 space-y-1.5 text-[12px] text-slate-700">
                  <PreviewRow icon={MapPin}   label="Market"     value={market || "—"} />
                  <PreviewRow icon={Filter}   label="Preset"     value={activeFilter.label} />
                  <PreviewRow icon={TrendingUp} label="Quantity" value={`${quantity} lead${quantity === 1 ? "" : "s"}`} />
                </div>
                <div className="rounded-lg bg-white border border-slate-200 p-3 text-[11.5px] text-slate-600 leading-relaxed">
                  Each lead lands in your Leads CRM tagged{" "}
                  <code className="text-amber-700 font-bold">marketplace</code> ·{" "}
                  <code className="text-amber-700 font-bold">{activeFilter.key}</code> — ready
                  for the Power Dialer.
                </div>
              </div>
            ) : (
              <div className="text-[12px] text-slate-500">Pick a filter to preview.</div>
            )}
          </div>
        </div>
      </section>

      {/* History */}
      {(history.topups.length > 0 || history.redemptions.length > 0) && (
        <section
          data-testid="marketplace-history"
          className="rounded-2xl border border-slate-200/70 bg-white shadow-sm overflow-hidden"
        >
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
            <History className="w-4 h-4 text-slate-500" />
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
              Recent activity
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-100">
            <div>
              <div className="px-5 py-2 text-[10px] uppercase tracking-widest font-bold text-emerald-700 bg-emerald-50/30">
                Purchases
              </div>
              {history.topups.length === 0 ? (
                <div className="px-5 py-4 text-[12px] text-slate-500">No purchases yet.</div>
              ) : (
                <div className="divide-y divide-slate-100 max-h-[280px] overflow-y-auto">
                  {history.topups.slice(0, 8).map((t) => (
                    <div key={t.id}
                         data-testid={`marketplace-topup-${t.id}`}
                         className="px-5 py-2.5 flex items-center gap-2">
                      <span className={`h-1.5 w-1.5 rounded-full ${
                        t.status === "paid" ? "bg-emerald-500" : "bg-amber-500"
                      }`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-[12.5px] font-bold text-slate-900">
                          +{t.credits} credits
                        </div>
                        <div className="text-[10.5px] text-slate-500">
                          ${(t.amount_cents / 100).toFixed(0)} · {t.status} · {timeAgo(t.created_at)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <div className="px-5 py-2 text-[10px] uppercase tracking-widest font-bold text-amber-700 bg-amber-50/30">
                Redemptions
              </div>
              {history.redemptions.length === 0 ? (
                <div className="px-5 py-4 text-[12px] text-slate-500">No redemptions yet.</div>
              ) : (
                <div className="divide-y divide-slate-100 max-h-[280px] overflow-y-auto">
                  {history.redemptions.slice(0, 8).map((r) => {
                    const verified = (r.ai_verified_count || 0) > 0;
                    const canVerify = subscription && (subscription.ai_verified_per_month || 0) > 0 && !verified;
                    return (
                      <div key={r.id}
                           data-testid={`marketplace-redemption-${r.id}`}
                           className="px-5 py-2.5 flex items-center gap-2">
                        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                        <div className="flex-1 min-w-0">
                          <div className="text-[12.5px] font-bold text-slate-900 flex items-center gap-1.5">
                            −{r.credits_used} credits · {r.leads_delivered ?? r.credits_used} leads
                            {verified && (
                              <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-widest font-black px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700"
                                    data-testid={`marketplace-redemption-${r.id}-verified`}>
                                <Sparkles className="w-2.5 h-2.5" /> {r.ai_verified_count} AI-verified
                              </span>
                            )}
                          </div>
                          <div className="text-[10.5px] text-slate-500">
                            {r.filter_preset} · {r.market} · {timeAgo(r.created_at)}
                          </div>
                        </div>
                        {canVerify && (
                          <button type="button"
                                  onClick={() => verifyRedemption(r.id)}
                                  disabled={verifyBusy === r.id}
                                  data-testid={`marketplace-redemption-${r.id}-verify-btn`}
                                  title={`Run AI verification (up to ${subscription.ai_verified_per_month}/mo)`}
                                  className="inline-flex items-center gap-0.5 rounded-md bg-purple-600 hover:bg-purple-700 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 disabled:opacity-60">
                            {verifyBusy === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                            Verify
                          </button>
                        )}
                        {verified && (
                          <button type="button"
                                  onClick={() => openSmsModal(r.id)}
                                  data-testid={`marketplace-redemption-${r.id}-sms-btn`}
                                  title="Send AI opener as SMS"
                                  className="inline-flex items-center gap-0.5 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1">
                            <MessageSquare className="w-3 h-3" />
                            SMS
                          </button>
                        )}
                        <Link to="/v3/crm/leads"
                              className="text-[11px] text-amber-600 hover:text-amber-700 font-semibold inline-flex items-center gap-0.5">
                          View <ArrowUpRight className="w-3 h-3" />
                        </Link>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </section>
      )}
      {smsRedemption && (
        <SmsBlastModal
          loading={smsLoading}
          leads={smsRedemption.leads}
          onClose={() => setSmsRedemption(null)}
        />
      )}
    </div>
  );
}

function PlanCard({ plan, isActive, busy, onSubscribe }) {
  const gradient = plan.popular
    ? "linear-gradient(135deg, #8B5CF6, #6D28D9)"
    : "linear-gradient(135deg, #1e293b, #0f172a)";
  return (
    <div
      data-testid={`marketplace-plan-${plan.key}`}
      className={`relative rounded-xl border-2 bg-white overflow-hidden transition-all hover:shadow-lg ${
        isActive ? "border-emerald-500 shadow-lg" :
        plan.popular ? "border-purple-400 shadow-md" : "border-slate-200 hover:border-purple-300"
      }`}
    >
      {plan.popular && !isActive && (
        <div className="absolute top-0 right-0 bg-purple-600 text-white text-[9px] uppercase tracking-widest font-black px-2 py-0.5 rounded-bl-lg">
          <Star className="w-2.5 h-2.5 inline mb-0.5" /> Popular
        </div>
      )}
      {isActive && (
        <div className="absolute top-0 right-0 bg-emerald-500 text-white text-[9px] uppercase tracking-widest font-black px-2 py-0.5 rounded-bl-lg">
          ✓ Active
        </div>
      )}
      <div className="p-4">
        <div className="text-[10px] uppercase tracking-widest font-bold text-purple-600">
          {plan.label}
        </div>
        <div className="mt-1 text-[26px] font-black text-slate-900 tabular-nums leading-none">
          {plan.price_pretty}
        </div>
        <div className="mt-0.5 text-[11px] text-slate-500">
          {plan.credits_per_month} credits/mo · {plan.ai_verified_leads_per_month} AI-verified
        </div>
        <div className="mt-3 text-[11.5px] text-slate-600 leading-snug min-h-[40px]">
          {plan.tagline}
        </div>
        <ul className="mt-3 space-y-1 text-[11px] text-slate-700">
          {plan.features.map((f, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <CheckCircle2 className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
        <button type="button"
                onClick={onSubscribe}
                disabled={busy || isActive}
                data-testid={`marketplace-plan-${plan.key}-subscribe`}
                className="mt-4 w-full inline-flex items-center justify-center gap-1.5 rounded-lg text-white text-[12.5px] font-semibold px-3 py-2 shadow-md transition-transform hover:scale-[1.02] disabled:opacity-60"
                style={{ backgroundImage: isActive ? "linear-gradient(135deg, #10B981, #059669)" : gradient }}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> :
           isActive ? <CheckCircle2 className="w-4 h-4" /> : <Crown className="w-4 h-4" />}
          {isActive ? "Current plan" : "Subscribe"}
        </button>
      </div>
    </div>
  );
}

function PackCard({ pack, busy, onBuy }) {
  return (
    <div
      data-testid={`marketplace-pack-${pack.key}`}
      className={`relative rounded-xl border-2 bg-white overflow-hidden transition-all hover:shadow-lg ${
        pack.popular ? "border-amber-400 shadow-md" : "border-slate-200 hover:border-amber-300"
      }`}
    >
      {pack.popular && (
        <div className="absolute top-0 right-0 bg-amber-500 text-white text-[9px] uppercase tracking-widest font-black px-2 py-0.5 rounded-bl-lg">
          Popular
        </div>
      )}
      <div className="p-4">
        <div className="text-[10px] uppercase tracking-widest font-bold text-amber-600">
          {pack.label}
        </div>
        <div className="mt-1 text-[26px] font-black text-slate-900 tabular-nums leading-none">
          {pack.price_pretty}
        </div>
        <div className="mt-0.5 text-[11px] text-slate-500">
          {pack.per_lead_pretty} / lead
        </div>
        <div className="mt-3 text-[11.5px] text-slate-600 leading-snug min-h-[40px]">
          {pack.tagline}
        </div>
        <button type="button"
                onClick={onBuy}
                disabled={busy}
                data-testid={`marketplace-pack-${pack.key}-buy`}
                className={`mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-lg text-white text-[12.5px] font-semibold px-3 py-2 shadow-md transition-transform hover:scale-[1.02] disabled:opacity-60 ${
                  pack.popular ? "" : ""
                }`}
                style={{
                  backgroundImage: pack.popular
                    ? "linear-gradient(135deg, #F59E0B, #B45309)"
                    : "linear-gradient(135deg, #1e293b, #0f172a)",
                }}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingCart className="w-4 h-4" />}
          Buy pack
        </button>
      </div>
    </div>
  );
}

function FormGroup({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">
        {label}
      </div>
      {children}
    </label>
  );
}

function PreviewRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="w-3 h-3 text-slate-400 shrink-0" />
      <div className="text-[10.5px] uppercase tracking-widest font-bold text-slate-500 w-16">
        {label}
      </div>
      <div className="text-[12px] font-semibold text-slate-900 truncate">{value}</div>
    </div>
  );
}


// ─── SMS Blast modal ────────────────────────────────────────────────────
// Lists each AI-verified lead from a redemption with an editable phone +
// pre-filled opener. Firing "Send" hits /api/twilio/send directly so all
// A2P / DNC / TCPA gates from the main SMS pipeline apply automatically.
function SmsBlastModal({ loading, leads, onClose }) {
  return (
    <div
      data-testid="marketplace-sms-modal"
      className="fixed inset-0 z-[80] bg-slate-900/70 backdrop-blur-sm grid place-items-center p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center gap-2 px-5 py-4 border-b border-slate-100 bg-white">
          <div className="h-9 w-9 rounded-lg grid place-items-center text-white shadow-md shrink-0"
               style={{ backgroundImage: "linear-gradient(135deg, #10B981, #059669)" }}>
            <MessageSquare className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-600 leading-none">
              One-tap outbound
            </div>
            <div className="text-[15px] font-bold text-slate-900 leading-tight">
              Send AI-verified openers via SMS
            </div>
          </div>
          <button type="button" onClick={onClose}
                  data-testid="marketplace-sms-modal-close"
                  className="p-1 rounded text-slate-400 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5">
          {loading ? (
            <div className="p-10 grid place-items-center text-slate-500 text-sm">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : leads.length === 0 ? (
            <div className="text-[12px] text-slate-500 text-center py-8">
              No verified leads found in this redemption yet.
            </div>
          ) : (
            <div className="space-y-3">
              {leads.map((lead) => (
                <SmsRow key={lead.id} lead={lead} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SmsRow({ lead }) {
  const verified = (lead.ai_verification || {}).opener_generated;
  const openerSource = (lead.ai_verification || {}).opener_source;
  const [phone, setPhone] = useState(lead.seller_phone || "");
  const [body, setBody] = useState(verified || "");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [sequences, setSequences] = useState([]);
  const [enrolling, setEnrolling] = useState(false);
  const [enrolled, setEnrolled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/sequences").then((r) => {
      if (!cancelled) setSequences((r.data?.sequences || []).filter((s) => s.enabled === true));
    }).catch(() => { /* silent — user may not have any sequences yet */ });
    return () => { cancelled = true; };
  }, []);

  const send = async () => {
    if (!phone.trim()) { toast.error("Add a phone number first."); return; }
    if (!body.trim()) { toast.error("Message body can't be empty."); return; }
    setBusy(true);
    try {
      await api.post("/twilio/send", {
        lead_id: lead.id,
        to: phone.trim(),
        body: body.trim(),
      });
      setSent(true);
      toast.success("SMS fired — reply lands in your Twilio inbox.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : (detail && (detail.message || detail.error))
          || "Send failed — check Twilio settings.";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const enroll = async (sequenceId) => {
    if (!sequenceId || enrolling) return;
    setEnrolling(true);
    try {
      await api.post("/sequences/enroll", { sequence_id: sequenceId, lead_id: lead.id });
      setEnrolled(true);
      toast.success("Lead enrolled in follow-up sequence.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Enroll failed.");
    } finally { setEnrolling(false); }
  };

  if (!verified) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 text-[12px] text-slate-500"
           data-testid={`marketplace-sms-row-${lead.id}-unverified`}>
        <div className="font-bold text-slate-700">
          {lead.property_address || "—"} · {lead.city}, {lead.state}
        </div>
        <div className="mt-1">Not AI-verified yet — run Verify from the history row first.</div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border-2 border-emerald-100 bg-white p-4 space-y-3"
         data-testid={`marketplace-sms-row-${lead.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[13px] font-bold text-slate-900 truncate">
            {lead.seller_name || "Unknown owner"}
          </div>
          <div className="text-[11px] text-slate-500 truncate">
            {lead.property_address} · {lead.city}, {lead.state} {lead.zip_code}
          </div>
        </div>
        <div className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-black px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700"
             data-testid={`marketplace-sms-row-${lead.id}-source`}>
          <Sparkles className="w-2.5 h-2.5" />
          {openerSource === "claude" ? "Claude-drafted" :
           openerSource === "cache"  ? "Claude-drafted" :
           "AI-verified"}
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1 flex items-center gap-1">
          <Phone className="w-3 h-3" /> Send to
        </div>
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+1 555 123 4567"
          disabled={sent}
          data-testid={`marketplace-sms-row-${lead.id}-phone`}
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 disabled:bg-slate-50"
        />
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1 flex items-center gap-1">
          <MessageSquare className="w-3 h-3" /> Message · {body.length}/1600
        </div>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          disabled={sent}
          data-testid={`marketplace-sms-row-${lead.id}-body`}
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-[12.5px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 disabled:bg-slate-50 resize-none"
        />
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        {sequences.length > 0 && !enrolled ? (
          <div className="flex items-center gap-1.5">
            <select
              onChange={(e) => enroll(e.target.value)}
              disabled={enrolling || !sent}
              defaultValue=""
              data-testid={`marketplace-sms-row-${lead.id}-sequence-select`}
              title={sent ? "Add this lead to a follow-up sequence" : "Send the SMS first, then add to a follow-up sequence"}
              className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11.5px] font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <option value="" disabled>+ Add to sequence…</option>
              {sequences.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            {enrolling && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
          </div>
        ) : enrolled ? (
          <div className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600"
               data-testid={`marketplace-sms-row-${lead.id}-enrolled`}>
            <CheckCircle2 className="w-3.5 h-3.5" /> Enrolled in sequence
          </div>
        ) : <div />}

        {sent ? (
          <div className="inline-flex items-center gap-1.5 text-[12px] font-bold text-emerald-600"
               data-testid={`marketplace-sms-row-${lead.id}-sent`}>
            <CheckCircle2 className="w-4 h-4" /> Sent
          </div>
        ) : (
          <button
            type="button"
            onClick={send}
            disabled={busy || !phone.trim() || !body.trim()}
            data-testid={`marketplace-sms-row-${lead.id}-send-btn`}
            className="inline-flex items-center gap-1.5 rounded-lg text-white text-[12.5px] font-semibold px-4 py-2 shadow-md disabled:opacity-60"
            style={{ backgroundImage: "linear-gradient(135deg, #10B981, #059669)" }}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send SMS
          </button>
        )}
      </div>
    </div>
  );
}
